<template>
  <Modal class="max-w-[500px]!">
    <Modal.Body>
      <ShieldIcon class="mx-auto mb-2.5 size-6 stroke-gray-800" />

      <h2
        class="mb-[18px] text-center text-[19px] font-medium leading-[26px] text-gray-800"
      >
        Confirmation of right to register work
      </h2>

      <p class="text-[11px] leading-[17px] text-gray-800">
        By continuing, I represent and warrant that I own or control the
        copyright (or exclusive rights) in the work(s) submitted, or I am duly
        authorized to submit them for the rights holder, and that Dacr may rely
        on this representation. Submitting work without rights or providing
        false information may be unlawful (including fraud) and may also expose
        me to civil and criminal liability under applicable law.
      </p>

      <div class="my-[14px] h-px w-full bg-[#DFDFDF]" />

      <div
        class="rounded-xl border border-[#EEEDEC] bg-[#F6F6F6] px-[14px] pt-2 pb-2.5"
      >
        <div class="flex items-center gap-1">
          <span class="text-[13px] font-medium leading-[17px] text-gray-800">
            Selected jurisdiction to be registered
          </span>
          <InfoSmallIcon class="size-3.5 text-gray-400" />
        </div>

        <div class="mt-2.5 flex items-center justify-between">
          <div class="flex items-center">
            <Avatar
              v-for="origin in selectedOrigins"
              :key="origin.id"
              :src="origin.flag?.url"
              :size="32"
              class="z-10 -mr-3 border-[3px] border-[#F6F6F6]"
            />
            <button
              type="button"
              class="group flex size-8 cursor-pointer items-center justify-center rounded-full border border-dashed border-gray-300/50 transition-all hover:border-gray-300/75"
              @click="handleChange"
            >
              <AddIcon
                class="size-3 text-gray-400 transition-all group-hover:text-gray-500"
              />
            </button>
          </div>

          <button
            type="button"
            class="text-link text-[13px]"
            @click="handleChange"
          >
            Change
          </button>
        </div>
      </div>

      <div
        class="mt-2 flex flex-col gap-y-1.5 rounded-xl border border-[#EEEDEC] px-[14px] py-[7px]"
      >
        <div class="flex items-center justify-between">
          <span class="text-[13px] leading-[18px] text-gray-600">
            Works being registered
          </span>
          <span class="text-[14px] leading-[18px] text-gray-800">
            {{ workCount }}
          </span>
        </div>

        <div class="flex items-center justify-between">
          <div class="flex items-center gap-x-2">
            <span class="text-[13px] font-medium leading-[18px] text-gray-800">
              Total to be billed
            </span>
            <button
              type="button"
              class="flex items-center gap-0.5 text-link text-[13px]"
              @click="handleDetails"
            >
              Details
              <ChevronRightIcon class="size-3" />
            </button>
          </div>
          <span class="text-[17px] font-medium leading-[18px] text-gray-800">
            {{ totalCost }}
          </span>
        </div>
      </div>

      <div class="mt-[14px] flex items-start gap-x-2">
        <Checkbox
          v-model="agreeToTerms"
          :class="['mt-px shrink-0', { 'cb-attention': !agreeToTerms }]"
        />
        <p
          class="cursor-pointer text-[12px] leading-[17px] text-gray-600"
          @click="agreeToTerms = !agreeToTerms"
        >
          I represent and warrant I have the rights/authorization to submit
          these work(s), and agree to the
          <a class="text-link" href="/legal/terms" target="_blank" @click.stop
            >Dacr Terms</a
          >,
          <a class="text-link" href="/legal/privacy" target="_blank" @click.stop
            >Privacy Policy</a
          >, and
          <a
            class="text-link"
            href="/legal/payments"
            target="_blank"
            @click.stop
            >Payments Policy</a
          >. All fees are final except where required by law.
        </p>
      </div>

      <Button
        variant="primary"
        size="lg"
        class="mt-2.5 w-full"
        :disabled="disableButton"
        :loading="loading"
        @click="save"
      >
        Agree, continue
      </Button>
      <button
        type="button"
        class="mx-auto mt-2 block text-link text-[14px]"
        @click="closeAll()"
      >
        Cancel
      </button>
    </Modal.Body>
  </Modal>
</template>

<script setup lang="ts">
import { Modal, Button, Checkbox, Avatar, useModal } from '@dacr/library'
import { AddIcon, ChevronRightIcon, InfoSmallIcon } from '@dacr/icons'
import ShieldIcon from '@/assets/svg/icons/ShieldIcon.svg'
import { openModal, Modals } from '@/shared/dialogs/modals'
import type { File } from '@/shared/graphql/router/graphql'
import { useConfirmRegisterCountries } from './useConfirmRegisterCountries'

const props = defineProps<{
  files: File[]
  originIds: string[]
}>()

const { close, closeAll } = useModal()

const {
  workCount,
  selectedOrigins,
  estimatedCost,
  totalCost,
  agreeToTerms,
  disableButton,
  loading,
  save,
} = useConfirmRegisterCountries(props.files, props.originIds, closeAll)

const handleChange = () => close()

const handleDetails = () =>
  openModal(Modals.ConfirmUploadDetails, {
    stack: true,
    props: { estimatedCost: estimatedCost.value },
  })
</script>

<style scoped>
.cb-attention :deep(span) {
  border-color: var(--color-green-500);
  box-shadow: 0 0 0 2px
    color-mix(in srgb, var(--color-green-200) 50%, transparent);
}
</style>
